/**
 * ProtectedRoute — gate for everything that needs a usable account.
 *
 * Three states, never two: `loading` (we simply do not know yet), signed out,
 * signed in. Collapsing the first into "signed out" makes the login screen
 * flash on every reload.
 */
import { Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { AccountGate } from "@/components/AccountGate";

function FullScreenSpinner() {
  return (
    <div
      data-testid="auth-loading"
      className="min-h-screen flex items-center justify-center bg-surface-subtle"
    >
      <Loader2 className="w-6 h-6 animate-spin text-brand-accent" />
    </div>
  );
}

export function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) return <FullScreenSpinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.status !== "active") return <AccountGate status={user.status} />;
  return children;
}

/**
 * AdminRoute — admin console access.
 *
 * Deliberately does NOT require a Google session: the console must stay
 * reachable through the break-glass password when Google or the owner's
 * account is unavailable. `AdminConsole` shows the password form when it
 * receives `authorized={false}`.
 */
export function AdminRoute({ children }) {
  const { loading } = useAuth();
  if (loading) return <FullScreenSpinner />;
  return children;
}

export default ProtectedRoute;
